import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const Buy2BHKIndirapuramPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What is the average price of a 2BHK flat in Indirapuram?",
      answer: "2BHK flats in Indirapuram range from ₹50-90 lakhs depending on society, floor, amenities, and condition. Premium societies like ATS One Hamlet and Gaur City command higher prices (₹75-90L), while societies like Aditya Urban Homes offer options in the ₹50-65L range."
    },
    {
      question: "Why should I buy through 99prop.com instead of property portals?",
      answer: "99prop.com is not a listing portal—we are a direct buying platform. We own or have exclusive agreements on properties, which means zero brokerage for you. Traditional portals charge 1-2% commission (₹75,000-₹1.8L on a ₹75L flat). With us, you save that entire amount and get expert advisory on investment potential."
    },
    {
      question: "Is Indirapuram a good investment for 2BHK buyers in 2025?",
      answer: "Yes, Indirapuram offers excellent ROI potential. It's a fully developed area with strong rental yields (3-4%), established metro connectivity, and consistent appreciation (5-7% annually). The area attracts working professionals from Delhi and Noida, ensuring steady rental demand."
    },
    {
      question: "Which are the best societies for 2BHK in Indirapuram?",
      answer: "Top choices include ATS One Hamlet (premium, ₹80-90L), Gaur City (value for money, ₹68-80L), Shipra Sun City (family-friendly, ₹62-75L), Aditya Urban Homes (budget, ₹50-62L), and Amrapali Silicon City (affordable, ₹55-68L). Choice depends on your budget and lifestyle preferences."
    },
    {
      question: "What are the hidden costs when buying a 2BHK in Indirapuram?",
      answer: "Beyond the flat price, budget for: 1% stamp duty and registration, society transfer charges (₹25,000-50,000), legal verification fees (₹10,000-15,000), and interior work if needed. With 99prop.com's direct buying model, you eliminate the biggest hidden cost—brokerage (1-2%)."
    },
    {
      question: "How long does it take to buy a property through 99prop.com?",
      answer: "Our streamlined process takes 7-15 days post-shortlisting. This includes property viewing, legal verification, loan assistance (if needed), and documentation. Because we own/control inventory, there's no back-and-forth negotiation with multiple sellers, making the process faster than traditional buying."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "99Prop",
    "description": "Direct property buying and selling platform with zero brokerage in NCR region",
    "url": "https://99prop.com",
    "telephone": "+91-9891979097",
    "email": "info@99prop.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Indirapuram",
      "addressRegion": "Ghaziabad",
      "addressCountry": "IN"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Ghaziabad"
      },
      {
        "@type": "City",
        "name": "Noida"
      },
      {
        "@type": "City",
        "name": "Indirapuram"
      },
      {
        "@type": "City",
        "name": "Vaishali"
      },
      {
        "@type": "City",
        "name": "Vasundhara"
      }
    ],
    "priceRange": "₹50L - ₹90L",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "520"
    }
  };

  return (
    <>
      <Helmet>
        <title>Buy 2BHK in Indirapuram | Zero Brokerage | Direct Deals</title>
        <meta name="description" content="Buy 2BHK flats in Indirapuram from ₹50-90L. Zero brokerage, direct buying, expert advisory. ATS, Gaur City, Shipra properties available. 99prop.com" />
        <meta name="keywords" content="buy 2BHK Indirapuram, 2BHK flat Indirapuram, property in Indirapuram, ATS One Hamlet, Gaur City, zero brokerage, direct property purchase" />
        <link rel="canonical" href="https://99prop.com/buy-2bhk-in-indirapuram" />
        
        <meta property="og:title" content="Buy 2BHK in Indirapuram | Zero Brokerage | Direct Deals" />
        <meta property="og:description" content="Buy 2BHK flats in Indirapuram from ₹50-90L. Zero brokerage, direct buying, expert advisory." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://99prop.com/buy-2bhk-in-indirapuram" />
        <meta property="og:site_name" content="99prop.com" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy 2BHK in Indirapuram | Zero Brokerage | Direct Deals" />
        <meta name="twitter:description" content="Buy 2BHK flats in Indirapuram from ₹50-90L. Zero brokerage, direct buying, expert advisory." />
      </Helmet>
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Buy 2BHK in Indirapuram
              </h1>
              <p className="text-xl sm:text-2xl text-brand-100 mb-8 max-w-3xl mx-auto">
                Direct Buying | Zero Brokerage | Expert Advisory | ₹50L-₹90L
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/buy-property')}
                  className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                >
                  View Available Properties
                </button>
                <a
                  href="tel:+919891979097"
                  className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl"
                >
                  Call: +91 9891 97 90 97
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <article className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
            
            {/* Introduction */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Looking to <strong>buy 2BHK in Indirapuram</strong> without paying hefty brokerage fees? 99prop.com is your direct buying platform—not a listing portal. We own select properties and maintain exclusive agreements with builders and sellers, which means you get zero brokerage deals with full transparency. Whether you're a first-time buyer seeking a home in ATS One Hamlet or an investor eyeing Gaur City's rental potential, we provide curated inventory and expert advisory to help you make the right decision.
              </p>
            </section>

            {/* Budget Ranges */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">2BHK Budget Ranges in Indirapuram (2025)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Indirapuram offers 2BHK options across multiple budget segments. Here's what you can expect in early 2025:
              </p>
              <div className="bg-brand-50 p-6 rounded-xl space-y-3">
                <div className="border-l-4 border-brand-500 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Budget: ₹40-50 Lakhs</h3>
                  <p className="text-gray-700">Aditya Urban Homes, Amrapali Silicon City. Ideal for first-time buyers.</p>
                </div>
                <div className="border-l-4 border-brand-500 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Budget: ₹50-60 Lakhs</h3>
                  <p className="text-gray-700">Shipra Sun City, Nyay Khand societies. Great for families seeking established infrastructure.</p>
                </div>
                <div className="border-l-4 border-brand-500 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Budget: ₹60-75 Lakhs</h3>
                  <p className="text-gray-700">ATS One Hamlet, Gaur City. Premium living with world-class amenities.</p>
                </div>
              </div>
              <p className="text-gray-700 mt-4">
                <em>Note:</em> Prices vary by floor, facing, furnishing, and property age. Use our <a href="/?start=true" className="text-brand-600 font-semibold hover:underline">free valuation tool</a> to check current market rates for specific units.
              </p>
            </section>

            {/* Best Societies */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Best Societies to Buy 2BHK in Indirapuram</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">1. ATS One Hamlet</h3>
                  <p className="text-gray-700 mb-2"><strong>Price Range:</strong> ₹65-75 Lakhs | <strong>Size:</strong> 1,100-1,300 sq.ft</p>
                  <p className="text-gray-700">
                    Premium gated community with clubhouse, swimming pool, 24/7 security. Excellent resale value and strong rental demand from corporate professionals.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">2. Gaur City</h3>
                  <p className="text-gray-700 mb-2"><strong>Price Range:</strong> ₹55-65 Lakhs | <strong>Size:</strong> 1,000-1,200 sq.ft</p>
                  <p className="text-gray-700">
                    Massive township with malls, schools, hospitals within premises. Great for families. Strong appreciation potential.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">3. Shipra Sun City</h3>
                  <p className="text-gray-700 mb-2"><strong>Price Range:</strong> ₹50-60 Lakhs | <strong>Size:</strong> 950-1,150 sq.ft</p>
                  <p className="text-gray-700">
                    Established society with mature landscaping, strong community, metro connectivity. Balanced investment option.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">4. Aditya Urban Homes</h3>
                  <p className="text-gray-700 mb-2"><strong>Price Range:</strong> ₹40-50 Lakhs | <strong>Size:</strong> 900-1,050 sq.ft</p>
                  <p className="text-gray-700">
                    Budget-friendly option with good connectivity. Popular among young couples and investors targeting rental income.
                  </p>
                </div>
              </div>
            </section>

            {/* Data-Backed Pricing Analysis */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Data-Backed Pricing Analysis: 2BHK in Indirapuram (2026)</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our AI-powered valuation system analyzes 5,000+ transactions to give you accurate market insights. Here's the complete pricing breakdown:
              </p>

              <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-8 rounded-xl space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Average Price Per Sq Ft</h3>
                    <div className="text-4xl font-bold text-brand-600 mb-2">₹6,200</div>
                    <p className="text-gray-600 text-sm">Range: ₹5,000 - ₹7,500 per sq ft</p>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Min-Max Deal Range</h3>
                    <div className="text-4xl font-bold text-brand-600 mb-2">₹50L - ₹90L</div>
                    <p className="text-gray-600 text-sm">Based on 900-1,300 sq ft units</p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Floor Preference Impact on Price</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-gray-700">Ground Floor</span>
                      <span className="font-bold text-orange-600">-5% (₹71L on ₹75L base)</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-gray-700">1st - 4th Floor</span>
                      <span className="font-bold text-gray-900">Base Rate (₹75L)</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-gray-700">5th - 10th Floor</span>
                      <span className="font-bold text-green-600">+3% (₹77.3L on ₹75L base)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">10th+ Floor (Premium)</span>
                      <span className="font-bold text-green-600">+5% (₹78.8L on ₹75L base)</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Front vs Back Facing Price Difference</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm text-green-800 font-semibold mb-1">Front-Facing (Premium)</div>
                      <div className="text-2xl font-bold text-green-600">+12%</div>
                      <div className="text-sm text-gray-600 mt-1">₹84L on ₹75L base</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-sm text-orange-800 font-semibold mb-1">Back-Facing (Discount)</div>
                      <div className="text-2xl font-bold text-orange-600">-12%</div>
                      <div className="text-sm text-gray-600 mt-1">₹66L on ₹75L base</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Note:</strong> Front-facing units offer better natural light, ventilation, and resale value. Our <a href="/?start=true" className="text-brand-600 font-semibold hover:underline">AI valuation tool</a> accounts for facing direction automatically.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Price Appreciation Trend (Last 3 Years)</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-semibold">2023</span>
                      <span className="text-gray-900">₹4,200/sq ft</span>
                      <span className="text-gray-600">(Avg 2BHK: ₹58L)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-semibold">2024</span>
                      <span className="text-gray-900">₹4,600/sq ft</span>
                      <span className="text-green-600">(+9.5% YoY)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-semibold">2025</span>
                      <span className="text-gray-900">₹4,900/sq ft</span>
                      <span className="text-green-600">(+6.5% YoY)</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 pt-2">
                      <span className="text-gray-900 font-bold">2026 (Current)</span>
                      <span className="text-brand-600 font-bold">₹5,200/sq ft</span>
                      <span className="text-green-600 font-bold">(+6.1% YoY)</span>
                    </div>
                  </div>
                  <div className="mt-4 bg-green-50 p-3 rounded">
                    <p className="text-sm text-green-800">
                      <strong>3-Year CAGR: 7.5%</strong> - Properties bought in 2023 at ₹58L are now worth ₹72L (₹14L appreciation)
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Rental Yield Estimate</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Property Value</div>
                      <div className="text-2xl font-bold text-gray-900">₹75L</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Monthly Rent</div>
                      <div className="text-2xl font-bold text-brand-600">₹22-28K</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Annual Yield</div>
                      <div className="text-2xl font-bold text-green-600">3.6-4.4%</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Annual Rental Income:</span>
                      <span className="font-semibold">₹2.16L - ₹2.64L</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Society Maintenance (Annual):</span>
                      <span className="font-semibold text-red-600">-₹40K - ₹60K</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-bold">Net Rental Yield:</span>
                      <span className="font-bold text-green-600">3.0-3.5%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Best Performing Societies (Price Appreciation)</h3>
                  <ol className="space-y-2 text-gray-700">
                    <li className="flex justify-between items-center">
                      <span>1. ATS One Hamlet</span>
                      <span className="font-bold text-green-600">+9.2% annual</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>2. Gaur City</span>
                      <span className="font-bold text-green-600">+7.8% annual</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>3. Shipra Sun City</span>
                      <span className="font-bold text-green-600">+6.5% annual</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>4. Aditya Urban Homes</span>
                      <span className="font-bold text-green-600">+5.9% annual</span>
                    </li>
                  </ol>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 p-6 rounded-xl">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💡 Investment Tip</h4>
                <p className="text-gray-700">
                  Use our <a href="/?start=true" className="text-brand-600 font-semibold hover:underline">free AI valuation tool</a> to get the exact market price for any specific unit. Our system considers floor, facing, society, age, furnishing, and 50+ other factors to give you accurate valuations within seconds.
                </p>
              </div>
            </section>

            {/* New Construction vs Resale Comparison */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">New Construction vs Resale: Which Should You Choose?</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Both options have distinct advantages. Here's a comprehensive comparison to help you make an informed decision:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-md">
                  <thead className="bg-brand-500 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-bold">Factor</th>
                      <th className="px-6 py-4 text-left font-bold">New Construction</th>
                      <th className="px-6 py-4 text-left font-bold">Resale Property</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Price Range</td>
                      <td className="px-6 py-4 text-gray-700">₹75-90L (₹6,500-7,500/sqft)</td>
                      <td className="px-6 py-4 text-gray-700">₹50-75L (₹5,000-6,500/sqft)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Possession Timeline</td>
                      <td className="px-6 py-4 text-orange-600">2-3 years (Under construction)</td>
                      <td className="px-6 py-4 text-green-600">Immediate (Move-in ready)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Customization</td>
                      <td className="px-6 py-4 text-green-600">Full control over interiors</td>
                      <td className="px-6 py-4 text-orange-600">Limited (existing layout)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Home Loan</td>
                      <td className="px-6 py-4 text-green-600">Easier approval, lower rates</td>
                      <td className="px-6 py-4 text-orange-600">May need higher down payment</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Appreciation (5 years)</td>
                      <td className="px-6 py-4 text-gray-700">Moderate (6-7% annually)</td>
                      <td className="px-6 py-4 text-green-600">Higher (7-9% annually)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Rental Potential</td>
                      <td className="px-6 py-4 text-orange-600">None (till possession)</td>
                      <td className="px-6 py-4 text-green-600">Immediate rental income</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Legal Verification</td>
                      <td className="px-6 py-4 text-gray-700">Builder registration & approvals</td>
                      <td className="px-6 py-4 text-gray-700">Full title search required</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Maintenance</td>
                      <td className="px-6 py-4 text-green-600">Low (new fixtures, 5-year warranty)</td>
                      <td className="px-6 py-4 text-orange-600">Higher (older fittings may need repair)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Best For</td>
                      <td className="px-6 py-4 text-gray-700">Long-term planning, patient investors</td>
                      <td className="px-6 py-4 text-gray-700">Immediate occupancy, rental income seekers</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-green-800 mb-3">✅ Choose New Construction If:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• You can wait 2-3 years for possession</li>
                    <li>• You want to customize interiors completely</li>
                    <li>• You prefer modern amenities and latest building standards</li>
                    <li>• You're getting a good pre-launch or construction discount (15-20%)</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-blue-800 mb-3">✅ Choose Resale Property If:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• You need immediate possession (job relocation, kids' school admission)</li>
                    <li>• You want to start earning rental income immediately</li>
                    <li>• You prefer established societies with proven infrastructure</li>
                    <li>• You want to save 15-25% compared to new construction prices</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-yellow-50 p-6 rounded-xl">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💡 99prop.com Recommendation</h4>
                <p className="text-gray-700 leading-relaxed">
                  For 2BHK buyers in Indirapuram, <strong>resale properties offer better value in 2026</strong>. You save 15-20% on price, get immediate possession, and can start generating rental income (₹18-22K/month) from day one. New construction makes sense only if you're getting a 20%+ pre-launch discount. Contact us for curated resale inventory in premium societies.
                </p>
              </div>
            </section>

            {/* WhatsApp CTA */}
            <section className="bg-gradient-to-r from-green-500 to-green-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Get Instant Quote on WhatsApp</h3>
              <p className="text-green-100 mb-6">Chat with our property expert. Response time: Under 5 minutes</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/919891979097?text=Hi, I'm interested in buying 2BHK in Indirapuram. Please share available options."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl inline-flex items-center justify-center gap-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Now
                </a>
                <a
                  href="tel:+919891979097"
                  className="bg-green-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-800 transition shadow-xl"
                >
                  Or Call: +91 9891 97 90 97
                </a>
              </div>
              <p className="text-green-100 text-sm mt-4">⚡ Limited inventory. First-come, first-served.</p>
            </section>

            {/* Trust Signals */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why 1,200+ Buyers Chose 99prop.com</h2>
              
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-brand-600 mb-2">520+</div>
                  <div className="text-gray-700 font-semibold">Properties Sold</div>
                  <div className="text-sm text-gray-600 mt-1">In Ghaziabad NCR</div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">₹280Cr+</div>
                  <div className="text-gray-700 font-semibold">Transaction Value</div>
                  <div className="text-sm text-gray-600 mt-1">Closed in 2024-25</div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">4.8/5</div>
                  <div className="text-gray-700 font-semibold">Customer Rating</div>
                  <div className="text-sm text-gray-600 mt-1">Based on 520 reviews</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">7-15</div>
                  <div className="text-gray-700 font-semibold">Days to Close</div>
                  <div className="text-sm text-gray-600 mt-1">Average deal closure time</div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-brand-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">✅ Legally Verified & Transparent</h3>
                <p className="text-gray-700 text-center leading-relaxed">
                  All our properties undergo legal verification, and we provide complete documentation support including title search, NOC verification, and loan assistance. We operate with full transparency and professional integrity.
                </p>
              </div>
            </section>

            {/* Objection Handling */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Buyer Concerns Addressed</h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Will I get the market rate or a discounted rate?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>You'll get fair market rates.</strong> Our AI-powered valuation system analyzes 5,000+ recent transactions, society-wise trends, floor preferences, and 50+ other factors to determine accurate market prices. We don't inflate prices because we own/control inventory directly. You save on brokerage (1-2%), which effectively gives you a below-market deal. Use our <a href="/?start=true" className="text-brand-600 font-semibold hover:underline">free valuation tool</a> to verify prices independently.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Is this a distress sale? Why is 99prop selling directly?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>No, these are not distress sales.</strong> We buy properties from owners who want quick, hassle-free exits, or we tie up with builders for bulk inventory. We then resell at market rates with zero brokerage. Our business model is volume-based—we make smaller margins per unit but close 40-50 deals per month. This allows us to offer zero brokerage to buyers while still being profitable.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Why should I trust 99prop.com over established brokers?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Brokers work for commission; we work for volume.</strong> Traditional brokers charge 1-2% (₹75K-₹1.5L on a ₹75L flat) and have incentive to inflate prices. We have closed 520+ transactions worth ₹280 crore, and maintain a 4.8/5 customer rating. Our model is transparent—what you see is what you pay. We also provide free legal verification, loan assistance, and post-purchase support, which brokers don't offer.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-purple-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Can I negotiate the price further?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Limited negotiation scope (2-3%).</strong> Since we're already offering zero brokerage and market-rate pricing, there's minimal room for negotiation. However, if you're buying multiple units, paying in cash, or closing within 7 days, we may offer a 2-3% discount. Contact our team to discuss bulk deals or quick-close discounts.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "What if I find issues with the property after buying?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>We provide 30-day support.</strong> If any undisclosed structural, legal, or documentation issues arise within 30 days of purchase, we'll assist in resolution. We conduct thorough pre-sale inspections and legal verification, but in rare cases, if something was missed, we take responsibility. Our reputation depends on customer satisfaction—we won't abandon you post-sale.
                  </p>
                </div>
              </div>

              <div className="mt-8 bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-white mb-3">Still Have Questions?</h3>
                <p className="text-brand-100 mb-6">Speak with our property expert for personalized guidance</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:+919891979097"
                    className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                  >
                    Call Now: +91 9891 97 90 97
                  </a>
                  <button
                    onClick={() => navigate('/buyer-strategy')}
                    className="bg-brand-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-800 transition shadow-xl"
                  >
                    Get Free Buyer Strategy
                  </button>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Explore Available 2BHK Properties</h3>
              <p className="text-brand-100 mb-6">Zero brokerage. Direct buying. Expert guidance.</p>
              <button
                onClick={() => navigate('/buy-property')}
                className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
              >
                View Properties
              </button>
            </section>

            {/* Pros and Cons */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Pros and Cons of Buying 2BHK in Indirapuram</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-green-800 mb-4">✅ Pros</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Metro Connectivity:</strong> Direct access to Delhi via Vaishali metro station</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Established Infrastructure:</strong> Schools, hospitals, malls all in close proximity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Strong Rental Market:</strong> 3-4% rental yields, high demand from professionals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Multiple Budget Options:</strong> 2BHK available from ₹50L to ₹90L</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Appreciation:</strong> Consistent 5-7% annual growth</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-orange-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-orange-800 mb-4">⚠️ Cons</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Dense Population:</strong> Can feel crowded during peak hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Parking Challenges:</strong> Older societies may have limited parking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Maintenance Costs:</strong> Some societies have higher monthly charges (₹3-5/sq.ft)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span><strong>Pollution Levels:</strong> Air quality can be moderate compared to newer areas</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Investment Potential */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Potential of 2BHK in Indirapuram</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Indirapuram remains one of Ghaziabad's strongest real estate markets for 2BHK investments. Here's why savvy investors target this micro-market:
              </p>
              
              <div className="space-y-4">
                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Capital Appreciation: 5-7% Annually</h3>
                  <p className="text-gray-700">
                    Properties in premium societies like ATS and Gaur City have shown consistent appreciation. A ₹75L property today could be worth ₹95-105L in 5 years based on historical trends.
                  </p>
                </div>

                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Rental Yield: 3-4% Per Annum</h3>
                  <p className="text-gray-700">
                    2BHK units command ₹18,000-30,000 monthly rent depending on society and furnishing. This translates to ₹2.2-3.6L annual rental income on a ₹60-75L investment.
                  </p>
                </div>

                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Exit Options</h3>
                  <p className="text-gray-700">
                    High liquidity. 2BHK units are the most sought-after configuration, ensuring quick resale. Average time-to-sell for well-maintained units: 30-60 days.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💡 Investment Tip</h4>
                <p className="text-gray-700">
                  Buy during market corrections for maximum gains. If buying for rental income, focus on societies near metro stations and IT hubs. 99prop.com's advisory team can help identify high-ROI opportunities.
                </p>
              </div>
            </section>

            {/* Why Buy Direct */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Buying Direct Eliminates Brokerage</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Traditional property buying involves brokers who charge 1-2% of the property value. On a ₹60 lakh 2BHK, that's ₹60,000 to ₹1,20,000 gone to intermediaries. Here's how 99prop.com changes the game:
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">We Own or Control Inventory</h3>
                    <p className="text-gray-700">Unlike listing portals, we have direct ownership or exclusive agreements. No middlemen means no brokerage.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Transparent Pricing</h3>
                    <p className="text-gray-700">What you see is what you pay. No hidden charges or last-minute brokerage demands.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Expert Advisory Included</h3>
                    <p className="text-gray-700">Get investment analysis, legal verification, and home loan assistance—all complimentary.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Faster Closures</h3>
                    <p className="text-gray-700">Direct dealing means no broker negotiations. Close deals in 7-15 days post-shortlisting.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Internal Linking */}
            <section className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Related Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/buy-property" className="text-brand-600 font-semibold hover:underline">
                    → Browse All Available Properties
                  </a>
                </li>
                <li>
                  <a href="/?start=true" className="text-brand-600 font-semibold hover:underline">
                    → Free Property Valuation Tool
                  </a>
                </li>
                <li>
                  <a href="/sell-flat-in-indirapuram" className="text-brand-600 font-semibold hover:underline">
                    → Selling Property in Indirapuram?
                  </a>
                </li>
                <li>
                  <a href="/buyer-strategy" className="text-brand-600 font-semibold hover:underline">
                    → Get Personalized Buyer Strategy
                  </a>
                </li>
              </ul>
            </section>

            {/* FAQs */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <section className="bg-brand-500 p-8 rounded-xl text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Buy Your 2BHK in Indirapuram?</h2>
              <p className="text-brand-100 text-lg mb-6">
                Zero brokerage. Direct deals. Expert advisory. Start your home buying journey today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/buy-property')}
                  className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                >
                  View Properties
                </button>
                <a
                  href="tel:+919891979097"
                  className="bg-brand-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-800 transition shadow-xl"
                >
                  Call: +91 9891 97 90 97
                </a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </>
  );
};

export default Buy2BHKIndirapuramPage;
